// Portal step 2: the whole application, ported from Buy Flow.dc.html. Emits
// onSubmit(answers) with the exact shape the public quote endpoint consumes.
// The unlicensed-state gate replaces the CTA (addendum §4) and captures a
// waitlist email through POST /api/ctr/waitlist (kind 'student').
import { useState } from 'react';
import { api } from '@/lib/api.js';

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
  DE: 'Delaware', DC: 'the District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine',
  MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
  NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia',
  WI: 'Wisconsin', WY: 'Wyoming',
};
const CERTS = ['None', 'Student', 'Recreational', 'Sport', 'Private', 'Commercial', 'ATP'];
const RATINGS = ['Instrument', 'ASEL', 'AMEL', 'ASES', 'Helicopter', 'Glider'];

const INPUT = 'min-h-[48px] w-full rounded-[11px] border-[1.5px] border-line bg-white px-3.5 text-ink outline-none focus:border-accent';
const LABEL = 'mb-1.5 block text-xs font-semibold text-ink-soft';
const EYEBROW = 'mb-3 text-[11px] font-bold uppercase tracking-[0.1em] text-accent';

function Chip({ on, children, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={`min-h-[44px] rounded-pill border-[1.5px] px-4 text-[13.5px] font-semibold ${on ? 'border-accent bg-teal-fill text-primary' : 'border-line bg-white text-ink-dim'}`}>
      {children}
    </button>
  );
}

function YesNo({ value, onChange }) {
  return (
    <div className="flex gap-2.5">
      {['No', 'Yes'].map((label) => {
        const on = value === (label === 'Yes');
        return (
          <button key={label} type="button" onClick={() => onChange(label === 'Yes')}
            className={`min-h-[44px] flex-1 rounded-[11px] border-[1.5px] text-sm font-bold ${on ? 'border-accent bg-teal-fill text-primary' : 'border-line bg-white text-ink-dim'}`}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function AboutYou({ school, licensedStates, quotingEnabled, quoteError, onSubmit, onBack, cfiPreset = false }) {
  const [a, setA] = useState({
    first_name: '', last_name: '', address_line: '', state: '', email: '', phone: '', dob: '',
    certificate: cfiPreset ? 'Commercial' : 'Student', is_cfi: cfiPreset, ratings: [],
    hours_total: '', hours_12mo: '', tailwheel_hours: '',
    uw_violations: false, uw_accidents: false, uw_details: '',
    member_number: '', coverage_start: new Date().toISOString().slice(0, 10),
  });
  const set = (patch) => setA((prev) => ({ ...prev, ...patch }));
  const [waitEmail, setWaitEmail] = useState('');
  const [onList, setOnList] = useState(false);

  const gated = Array.isArray(licensedStates) && a.state && !licensedStates.includes(a.state);
  const needsFleetFlags = school?.aircraft_types || {};
  const canSubmit = quotingEnabled && !gated && a.first_name && a.last_name && a.address_line && a.state && a.email && a.dob;

  async function joinWaitlist() {
    try {
      await api.post('/api/ctr/waitlist', { kind: 'student', email: waitEmail || a.email, state: a.state, school_slug: school?.slug || null, school_name: school?.name || null });
      setOnList(true);
    } catch { /* the button stays; a failed join can be retried */ }
  }

  return (
    <>
      <h1 className="mb-1.5 mt-1.5 text-[23px] font-bold tracking-[-0.02em]">About you</h1>
      <p className="mb-5 text-sm leading-[1.55] text-ink-dim">This is the whole application. Ten questions, most of them short.</p>

      <div className={EYEBROW}>Who you are</div>
      <div className="mb-3 grid grid-cols-2 gap-3">
        <input placeholder="First name" autoComplete="given-name" className={INPUT} value={a.first_name} onChange={(e) => set({ first_name: e.target.value })} />
        <input placeholder="Last name" autoComplete="family-name" className={INPUT} value={a.last_name} onChange={(e) => set({ last_name: e.target.value })} />
      </div>
      <div className="mb-3">
        <input placeholder="Home address" className={INPUT} value={a.address_line} onChange={(e) => set({ address_line: e.target.value })} />
        <div className="mt-1.5 flex items-center gap-2">
          <select value={a.state} onChange={(e) => { set({ state: e.target.value }); setOnList(false); }}
            className="min-h-[48px] flex-none rounded-[11px] border-[1.5px] border-line bg-white px-3 text-ink outline-none focus:border-accent">
            <option value="">State</option>
            {Object.keys(STATE_NAMES).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="text-[11.5px] text-ink-faint">Your state is part of your price; the rest goes on your policy documents.</div>
        </div>
      </div>
      {gated ? (
        <div className="mb-3.5 rounded-card-lg bg-warn-bg p-[14px_16px] text-[13px] leading-[1.6] text-warn-deep">
          <b>Coverage isn&apos;t available in {STATE_NAMES[a.state]} yet.</b> We can&apos;t quote there today (producer
          licensing; it&apos;s in progress). Leave your email and we&apos;ll tell you the day it opens{school?.name ? <>; {school.name} can still track a policy you buy elsewhere</> : null}.
          {onList ? (
            <div className="mt-2.5 font-bold">✓ You&apos;re on the list. We&apos;ll email you the day it opens.</div>
          ) : (
            <>
              <input placeholder="you@example.com" type="email" value={waitEmail} onChange={(e) => setWaitEmail(e.target.value)}
                className="mt-2.5 min-h-[46px] w-full rounded-[10px] border-[1.5px] border-amber bg-white px-[13px] text-sm outline-none" />
              <button type="button" onClick={joinWaitlist}
                className="mt-2 min-h-[46px] w-full rounded-[10px] bg-warn-deep text-sm font-bold text-white">
                Tell me when it&apos;s available
              </button>
            </>
          )}
        </div>
      ) : null}
      <div className="mb-3 grid grid-cols-2 gap-3">
        <input placeholder="Email" type="email" autoComplete="email" className={INPUT} value={a.email} onChange={(e) => set({ email: e.target.value })} />
        <input placeholder="Phone" type="tel" autoComplete="tel" className={INPUT} value={a.phone} onChange={(e) => set({ phone: e.target.value })} />
      </div>
      <div className="mb-6">
        <label className={LABEL}>Date of birth</label>
        <input type="date" className={INPUT} value={a.dob} onChange={(e) => set({ dob: e.target.value })} />
      </div>

      <div className={EYEBROW}>Your flying</div>
      <div className="mb-3.5">
        <label className={LABEL}>Pilot certificate</label>
        <div className="flex flex-wrap gap-2">
          {CERTS.map((c) => <Chip key={c} on={a.certificate === c} onClick={() => set({ certificate: c })}>{c}</Chip>)}
        </div>
        <label className="mt-2.5 flex min-h-[44px] cursor-pointer items-center gap-[9px] text-[13px] text-ink-soft">
          <input type="checkbox" className="h-[17px] w-[17px]" checked={a.is_cfi} onChange={(e) => set({ is_cfi: e.target.checked })} />
          I also hold a flight instructor certificate (CFI)
        </label>
      </div>
      <div className="mb-3.5">
        <label className={LABEL}>Ratings <span className="font-normal text-ink-faint">(skip if none yet)</span></label>
        <div className="flex flex-wrap gap-2">
          {RATINGS.map((r) => (
            <Chip key={r} on={a.ratings.includes(r)}
              onClick={() => set({ ratings: a.ratings.includes(r) ? a.ratings.filter((x) => x !== r) : [...a.ratings, r] })}>
              {r}
            </Chip>
          ))}
        </div>
      </div>
      <div className="mb-3 grid grid-cols-2 gap-3">
        <div><label className={LABEL}>Total hours</label><input inputMode="numeric" className={INPUT} value={a.hours_total} onChange={(e) => set({ hours_total: e.target.value })} /></div>
        <div><label className={LABEL}>Hours in last 12 months</label><input inputMode="numeric" className={INPUT} value={a.hours_12mo} onChange={(e) => set({ hours_12mo: e.target.value })} /></div>
      </div>
      {needsFleetFlags.tailwheel ? (
        <div className="mb-6">
          <label className={LABEL}>Tailwheel hours <span className="font-normal text-ink-faint">({school.name}&apos;s fleet includes tailwheel)</span></label>
          <input inputMode="numeric" className={INPUT} value={a.tailwheel_hours} onChange={(e) => set({ tailwheel_hours: e.target.value })} />
        </div>
      ) : null}

      <div className={EYEBROW}>Two questions insurers ask</div>
      <div className="mb-3 rounded-[13px] border-[1.5px] border-line bg-white p-[16px_18px]">
        <div className="mb-3 text-[13.5px] leading-[1.55]">
          In the last 5 years, have you been cited for violation of any aviation regulation, had a pilot&apos;s or
          driver&apos;s license suspended, been convicted of DUI, or convicted of a felony?
        </div>
        <YesNo value={a.uw_violations} onChange={(v) => set({ uw_violations: v })} />
      </div>
      <div className="mb-3 rounded-[13px] border-[1.5px] border-line bg-white p-[16px_18px]">
        <div className="mb-3 text-[13.5px] leading-[1.55]">In the last 5 years, have you had any aircraft accidents, incidents, or losses?</div>
        <YesNo value={a.uw_accidents} onChange={(v) => set({ uw_accidents: v })} />
      </div>
      {a.uw_violations || a.uw_accidents ? (
        <div className="mb-3">
          <label className={LABEL}>Tell us what happened, briefly</label>
          <textarea placeholder="A sentence or two is plenty. An underwriter reads this, not a robot."
            className="min-h-[88px] w-full resize-y rounded-[11px] border-[1.5px] border-line bg-white p-[12px_14px] leading-[1.5] outline-none focus:border-accent"
            value={a.uw_details} onChange={(e) => set({ uw_details: e.target.value })} />
        </div>
      ) : null}

      <div className="mb-3 mt-3 text-[11px] font-bold uppercase tracking-[0.1em] text-accent">Last things</div>
      <div className="mb-3 grid grid-cols-2 items-end gap-3">
        <div>
          <label className={LABEL}>AOPA / EAA / other member?</label>
          <input placeholder="Member number" className={INPUT} value={a.member_number} onChange={(e) => set({ member_number: e.target.value })} />
          <div className="mt-[5px] text-[11px] text-ink-faint">Optional, may earn a credit</div>
        </div>
        <div>
          <label className={LABEL}>Coverage starts</label>
          <input type="date" className={INPUT} value={a.coverage_start} onChange={(e) => set({ coverage_start: e.target.value })} />
          <div className="mt-[5px] text-[11px] text-ink-faint">Today to 90 days out</div>
        </div>
      </div>
      <button type="button" disabled={!canSubmit} onClick={() => onSubmit(a)}
        className="mt-2 min-h-[52px] w-full rounded-card-lg text-base font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
        style={{ background: 'var(--school-accent)' }}>
        {gated ? "Quotes aren't available in your state yet" : quotingEnabled ? 'Get my price' : 'Pricing…'}
      </button>
      {quoteError ? <div className="mt-2 text-center text-[12.5px] font-semibold text-warn-fg">{quoteError}</div> : null}
      <button type="button" onClick={onBack} className="mt-1.5 min-h-[44px] w-full text-[13.5px] font-semibold text-ink-dim">← Back</button>
    </>
  );
}
